import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { ApiError } from '../api/client.js';

export function LoadingState({ label = 'Loading' }: { label?: string }): React.JSX.Element {
  return (
    <Stack role="status" spacing={2} sx={{ alignItems: 'center', py: 8 }}>
      <CircularProgress size={32} />
      <Typography color="text.secondary">{label}…</Typography>
    </Stack>
  );
}

export function ErrorState({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}): React.JSX.Element {
  return (
    <Alert severity="error" action={retry ? <Button onClick={retry}>Try again</Button> : undefined}>
      {error instanceof Error ? error.message : 'Something went wrong.'}
    </Alert>
  );
}

export function EmptyState({
  title,
  detail,
}: {
  title: string;
  detail: string;
}): React.JSX.Element {
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Typography variant="h2">{title}</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        {detail}
      </Typography>
    </Box>
  );
}

export function PermissionDeniedState({
  resource = 'this page',
}: {
  resource?: string;
}): React.JSX.Element {
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Typography variant="h2">You do not have access</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        Your role does not include permission to view {resource}. Ask an administrator if you need
        it.
      </Typography>
    </Box>
  );
}

export function NotFoundState({ resource = 'record' }: { resource?: string }): React.JSX.Element {
  return (
    <Box sx={{ py: 7, textAlign: 'center' }}>
      <Typography variant="h2">This {resource} is not available</Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        It may have been removed, or the link may be wrong.
      </Typography>
    </Box>
  );
}

/** Picks the right state for a failed query: 401 is handled by the shell, so only 403, 404, and other errors arrive here. */
export function QueryFailureState({
  error,
  resource,
  retry,
}: {
  error: unknown;
  resource: string;
  retry?: () => void;
}): React.JSX.Element {
  if (error instanceof ApiError && error.status === 403) {
    return <PermissionDeniedState resource={resource} />;
  }
  if (error instanceof ApiError && error.status === 404) {
    return <NotFoundState resource={resource} />;
  }
  return <ErrorState error={error} {...(retry ? { retry } : {})} />;
}
